import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { getHotMovies, searchMovies } from "@/lib/movies";
import { GENRES } from "@/lib/categories";
import { useSeo } from "@/hooks/useSeo";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [text, setText] = useState(keyword);

  useEffect(() => {
    setText(keyword);
  }, [keyword]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = text.trim();
      if (trimmed === keyword) return;
      if (trimmed) {
        setSearchParams({ q: trimmed }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    }, 450);
    return () => window.clearTimeout(timer);
  }, [text, keyword, setSearchParams]);

  useSeo({
    title: keyword ? `“${keyword}”搜索结果 - 免费看大片` : "影片搜索 - 免费看大片在线观看平台",
    description: keyword
      ? `免费看大片中与“${keyword}”相关的影片搜索结果，可查看影片简介、评分、导演与主演信息，并直接进入详情页。`
      : "在免费看大片搜索最新热门电影与经典大片，支持按片名、导演、演员与类型模糊搜索。",
    keywords: keyword
      ? `${keyword},搜索${keyword},${keyword}电影,免费看大片`
      : "电影搜索,影片搜索,免费看大片",
  });

  const results = useMemo(() => searchMovies(keyword), [keyword]);
  const hotMovies = getHotMovies(5);
  const hasKeyword = keyword.trim().length > 0;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
      <div className="rounded-lg border border-background-200/70 bg-gradient-to-r from-primary-950/60 via-background-100 to-background-100 p-5 md:p-8">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-400">
          <span className="h-3 w-0.5 rounded-full bg-accent-500" />
          Search
        </p>
        <h1 className="mt-2 font-heading text-2xl font-semibold text-foreground-50 md:text-3xl">
          {hasKeyword ? `“${keyword}”的搜索结果` : "搜索影片"}
        </h1>
        <p className="mt-2 text-sm text-foreground-400">
          支持按片名、导演、演员、类型与年份模糊搜索，输入后会自动刷新结果。
        </p>

        <form
          onSubmit={handleSubmit}
          role="search"
          className="mt-5 flex w-full flex-col gap-2 sm:flex-row sm:items-center"
        >
          <div className="flex w-full items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3 transition-colors focus-within:border-primary-500/70">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              type="search"
              name="keyword"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="输入影片名称、导演或演员，例如：星环"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-600 md:text-base"
            />
            {text && (
              <button
                type="button"
                onClick={() => setText("")}
                aria-label="清空关键词"
                className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:text-foreground-100"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line text-base" />
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">热门搜索</span>
          {[...GENRES.slice(0, 4).map((genre) => genre.name), ...getHotMovies(3).map((movie) => movie.title)].map(
            (item) => (
              <button
                key={item}
                type="button"
                onClick={() => setText(item)}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300/80 px-3 py-1 text-xs text-foreground-300 transition-colors hover:border-primary-500/60 hover:text-primary-300"
              >
                {item}
              </button>
            ),
          )}
        </div>
      </div>

      {hasKeyword && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-foreground-400">
            找到
            <span className="mx-1 font-heading text-base font-semibold text-accent-400">
              {results.length}
            </span>
            部与“{keyword}”相关的影片
          </p>
          <Link
            to="/category/全部"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-400 transition-colors hover:text-primary-300"
          >
            浏览全部影片
            <i className="ri-arrow-right-line text-sm" />
          </Link>
        </div>
      )}

      <div className="mt-5">
        {hasKeyword && results.length > 0 && <MovieGrid movies={results} showSynopsis />}

        {hasKeyword && results.length === 0 && (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-400">
              <i className="ri-question-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-medium text-foreground-100">
              没有找到与“{keyword}”相关的影片
            </h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
              可以尝试更换关键词，或换个类型浏览。下面是近期播放量最高的几部影片，也许会有惊喜。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => setText("")}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-base" />
                重新搜索
              </button>
              <Link
                to="/category/全部?sort=hot"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
              >
                查看热门榜单
              </Link>
            </div>
          </div>
        )}

        {!hasKeyword && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100/40 px-6 py-12 text-center">
            <p className="font-heading text-base text-foreground-200">输入关键词开始搜索</p>
            <p className="mt-2 text-sm text-foreground-500">
              例如「星环」「悬疑」「林川」，搜索结果会即时显示在下方。
            </p>
          </div>
        )}
      </div>

      {(!hasKeyword || results.length === 0) && (
        <section className="mt-12">
          <SectionHeading
            eyebrow="Popular now"
            title="大家都在看"
            description="近期播放量最高的影片，不知道看什么时，从这里挑一部。"
            actionLabel="完整榜单"
            actionTo="/category/全部?sort=hot"
          />
          <MovieGrid movies={hotMovies} />
        </section>
      )}
    </div>
  );
}